import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Rocket, Search, Trash2 } from "lucide-react";
import {
  deleteDeployment,
  listDeployments,
  type Deployment,
} from "../api/deployments";
import Card from "../components/Card";
import Button from "../components/Button";
import Chip from "../components/Chip";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import StatusPill from "../components/StatusPill";
import { formatIst } from "../lib/format";

const RUNNING_STATUSES = new Set(["RUNNING", "STARTING", "STOPPING"]);

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "RUNNING", label: "Running" },
  { value: "STOPPED", label: "Stopped" },
  { value: "ERROR", label: "Error" },
];

const MODE_FILTERS = [
  { value: "", label: "All" },
  { value: "paper", label: "Paper" },
  { value: "live", label: "Live" },
];

export default function DeploymentsPage() {
  const { data: deployments, isLoading } = useQuery({
    queryKey: ["deployments"],
    queryFn: listDeployments,
    refetchInterval: 5_000,
  });

  const qc = useQueryClient();
  const navigate = useNavigate();
  const del = useMutation({
    mutationFn: (id: string) => deleteDeployment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deployments"] }),
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");

  const filtered = useMemo(() => {
    const list = deployments ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false;
      if (modeFilter && d.mode !== modeFilter) return false;
      if (q) {
        const hay = `${d.name} ${d.strategy_key}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [deployments, search, statusFilter, modeFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Deployments</h1>
          <p className="text-sm text-muted mt-0.5">
            Manage paper and live runners across your strategies.
          </p>
        </div>
        <Link to="/deployments/new">
          <Button iconLeft={<Plus size={14} />}>New deployment</Button>
        </Link>
      </div>

      <Card density="compact">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              placeholder="Search by name or strategy..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-surface border border-border rounded pl-8 pr-3 py-1.5 text-sm w-72"
            />
          </div>
          <FilterGroup
            value={statusFilter}
            options={STATUS_FILTERS}
            onChange={setStatusFilter}
            label="Status"
          />
          <FilterGroup
            value={modeFilter}
            options={MODE_FILTERS}
            onChange={setModeFilter}
            label="Mode"
          />
          <div className="ml-auto text-xs text-muted font-mono">
            {filtered.length} / {deployments?.length ?? 0}
          </div>
        </div>
      </Card>

      <Card>
        {isLoading && <div className="text-sm text-muted">Loading…</div>}
        {!isLoading && filtered.length === 0 && (
          <EmptyState
            icon={<Rocket size={32} />}
            title={deployments && deployments.length === 0 ? "No deployments yet" : "No matches"}
            message={
              deployments && deployments.length === 0
                ? "Create one to start backtesting or trading."
                : "Try clearing your filters."
            }
            action={
              deployments && deployments.length === 0 ? (
                <Link to="/deployments/new">
                  <Button iconLeft={<Plus size={14} />}>Create deployment</Button>
                </Link>
              ) : undefined
            }
          />
        )}
        {filtered.length > 0 && (
          <DataTable
            rows={filtered}
            rowKey={(d) => d.id}
            columns={[
              {
                key: "name",
                header: "Name",
                render: (d: Deployment) => (
                  <Link
                    to={`/deployments/${d.id}`}
                    className="font-medium text-fg hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    {d.name}
                  </Link>
                ),
              },
              {
                key: "strategy",
                header: "Strategy",
                mono: true,
                render: (d) => <span className="text-fg-subtle">{d.strategy_key}</span>,
              },
              {
                key: "mode",
                header: "Mode",
                render: (d) => (
                  <Chip tone={d.mode === "live" ? "warn" : "info"} mono>
                    {d.mode}
                  </Chip>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (d) => <StatusPill status={d.status} />,
              },
              {
                key: "updated",
                header: "Updated",
                render: (d) => (
                  <span className="text-xs text-muted font-mono">
                    {formatIst(d.updated_at)}
                  </span>
                ),
              },
              {
                key: "open",
                header: "",
                align: "right",
                render: (d) => {
                  const running = RUNNING_STATUSES.has(d.status);
                  return (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Copy this configuration into a new deployment, in paper or live"
                        onClick={() => navigate(`/deployments/${d.id}?clone=1`)}
                        iconLeft={<Copy size={14} />}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={running || del.isPending}
                        title={
                          running
                            ? "Stop the deployment before deleting it"
                            : "Delete this deployment and its history"
                        }
                        onClick={() => {
                          if (
                            confirm(
                              `Delete deployment "${d.name}"?\n\nThis also ` +
                                `removes its order history, fills and day ` +
                                `state. It cannot be undone.`,
                            )
                          )
                            del.mutate(d.id);
                        }}
                        iconLeft={<Trash2 size={14} />}
                      >
                        Delete
                      </Button>
                      <Link to={`/deployments/${d.id}`}>
                        <Button variant="ghost" size="sm">
                          Open →
                        </Button>
                      </Link>
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

function FilterGroup({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted uppercase tracking-wide">{label}:</span>
      <div className="inline-flex rounded border border-border overflow-hidden">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`px-2.5 py-1 text-xs transition-colors ${
              value === o.value
                ? "bg-brand-600 text-white"
                : "bg-surface text-fg-subtle hover:bg-surface-2"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
